<template>
  <Card class="position-relative">
    <template #title>
      <div class="align-items-center justify-content-between" :class="isMD ? '' : 'flex'">
        <img src="@/assets/personal-active.svg" />
        <div class="align-items-center" :class="isMD ? '' : 'flex'">
          <span class="p-input-icon-left" :class="isMD ? 'mt-2' : 'ml-2'">
            <i class="pi pi-search" />
            <InputText @input="getSearchString($event)" type="text" v-model="value1" placeholder="Search" />
          </span>
        </div>
      </div>
    </template>
    <template #content>
      <Spinner v-if="isLoading" class="flex" />
      <DataTable v-if="!isLoading && !isMD" :value="brokerList" :totalRecords="brokers.length">
        <Column sortable field="brokerReferenceNumber" header="ID" />
        <Column sortable field="fullName" header="Name" />
        <Column sortable field="company" header="Company" />
        <Column sortable field="email" header="Email" />
        <Column sortable field="mobile" header="Mobile" />
        <Column sortable field="state" header="State" />
        <Column field="id">
          <template #body="{ data }">
            <Button
              label="Submit application"
              type="primary"
              iconPos="right"
              icon="pi pi-chevron-right"
              @click="goToLoanApp(data.id)"
            />
          </template>
        </Column>
      </DataTable>
      <Paginator
        v-if="!isMD"
        class="mt-2"
        :rows="5"
        :totalRecords="brokersCount"
        :rowsPerPageOptions="[5, 10, 20, 30]"
        template="RowsPerPageDropdown  CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
        currentPageReportTemplate="Showing {first} of {totalRecords} brokers"
        @page="onChangePage($event)"
      />
    </template>
  </Card>
</template>

<script>
import { API, LOAN_APPLICATION_URL, TECHLEND_BE_URL } from '@/constants.api';
import { _get } from '@/services/crudService';
import { debounce } from 'debounce';
import { useCookies } from 'vue3-cookies';
import Card from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Paginator from 'primevue/paginator';
import ScreenSize from '@/mixins/screensize.mixin';

export default {
  name: 'index',
  mixins: [ScreenSize],
  components: {
    DataTable,
    Column,
    Card,
    InputText,
    Paginator,
  },
  data() {
    return {
      isLoading: true,
      brokers: [],
      brokersCount: 0,
      searchString: '',
      rowsPerPage: 5,
    };
  },
  computed: {
    brokerList() {
      return this.brokers.map((broker) => {
        return {
          id: broker.id,
          brokerReferenceNumber: broker.brokerReferenceNumber,
          fullName: [broker.firstName, broker.lastName].filter(Boolean).join(' '),
          company: broker.brokerFirm ?? '',
          email: broker.email,
          mobile: broker.mobilePhoneNumber,
          state: (broker.state ?? 'other').toUpperCase(),
        };
      });
    },
  },
  methods: {
    async doFilter() {
      await this.loadData(0, this.rowsPerPage, this.searchString);
    },
    getSearchString: debounce(async function ({ target }) {
      this.searchString = target.value;
      await this.doFilter();
    }, 800),
    async onChangePage(value) {
      this.rowsPerPage = Number(value.rows);
      await this.loadData(Number(value.page) * this.rowsPerPage, this.rowsPerPage, this.searchString);
    },
    async loadData(offset, limit, search) {
      this.isLoading = true;
      try {
        let query = `?offset=${offset}&limit=${limit}`;
        query += search ? `&search=${encodeURIComponent(search)}` : '';
        const { data } = await _get(TECHLEND_BE_URL + API.BROKER_ASSISTANTS.LIST_BROKERS + query);
        this.brokers = data?.brokers ?? [];
        this.brokersCount = Number(data?.count ?? 0);
      } finally {
        this.isLoading = false;
      }
    },
    goToLoanApp(brokerId) {
      const { cookies } = useCookies();
      const tokenKey = 'broker-id';

      // Resolve the loan application host using URL API (support host-only by prefixing scheme)
      const rawLoanUrl = LOAN_APPLICATION_URL || '';
      const loanUrl = new URL(rawLoanUrl.includes('://') ? rawLoanUrl : `https://${rawLoanUrl}`);
      const currentHost = window.location.hostname;
      const targetHost = loanUrl.hostname;

      // Derive base domain from the target host deterministically (supports .com.au)
      const normalizedTargetParts = targetHost.toLowerCase().split('.').filter(Boolean);
      let baseDomain = null;
      if (
        normalizedTargetParts.length >= 3 &&
        normalizedTargetParts[normalizedTargetParts.length - 1] === 'au' &&
        normalizedTargetParts[normalizedTargetParts.length - 2] === 'com'
      ) {
        // Prefer environment scoping for bridgit.com.au when env label is 'eph'
        if (
          normalizedTargetParts.length >= 4 &&
          normalizedTargetParts[normalizedTargetParts.length - 3] === 'bridgit'
        ) {
          const envLabel = normalizedTargetParts[normalizedTargetParts.length - 4];
          baseDomain = envLabel === 'eph' ? 'eph.bridgit.com.au' : 'bridgit.com.au';
        } else {
          baseDomain = normalizedTargetParts.slice(-3).join('.');
        }
      } else if (normalizedTargetParts.length >= 2) {
        // e.g., app.example.com -> example.com
        baseDomain = normalizedTargetParts.slice(-2).join('.');
      }

      // Only attempt cross-subdomain cookie if we're already under that base domain
      const currentHostLower = currentHost.toLowerCase();
      if (baseDomain && currentHostLower.endsWith(baseDomain)) {
        cookies.set(tokenKey, brokerId, null, '/', `.${baseDomain}`, true, 'None');
      } else {
        // Fallback for localhost or unrelated domains
        cookies.set(tokenKey, brokerId);
      }

      // Open target URL with brokerId appended safely
      loanUrl.searchParams.set('brokerId', brokerId);
      window.open(loanUrl.toString(), '_blank');
    },
  },
  async created() {
    await this.loadData(0, this.rowsPerPage);
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';
:deep(.p-card-body) {
  overflow: auto !important;
}
.p-card {
  box-shadow: unset !important;
  border-radius: 4px !important;
  color: $primary-text-color !important;
  font-family: $font-family !important;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: $white-color;
  color: rgba(2, 0, 56, 0.3) !important;
  font-size: 1rem;
}
:deep(.p-datatable .p-datatable-tbody > tr > td) {
  font-weight: 500 !important;
  font-size: 14px !important;
  color: $secondary-color !important;
  font-family: HK-medium !important;
  vertical-align: top !important;
}

:deep(.p-paginator .p-paginator-pages .p-paginator-page.p-highlight) {
  background: unset !important;
  color: $primary-color !important;
  border-color: unset !important;
}

:deep(.p-paginator .p-paginator-current) {
  color: rgba(2, 0, 56, 0.3);
  font-weight: 500;
  font-size: 14px;
}
</style>
